/**
 * referenceImageIngest — get a reference's images from their source URLs into
 * Storage WITHOUT routing the bytes through this browser.
 *
 * Bulk import used to `fetchImageBlob` each image (a doomed direct attempt, then
 * a proxy that answers in base64) and `uploadReferenceImage` it back out over
 * the curator's own connection. Measured on a 30-reference Breitling "Replace
 * all images" run: 114 PNGs, 204 MB, 17 minutes, three quarters of it upload.
 * The `ingestReferenceImages` callable does the fetch and the Storage write
 * inside the cloud instead; this module sends it URLs and gets paths back.
 *
 * The browser path is kept as a per-image FALLBACK, not removed: some hosts
 * refuse cloud egress but answer a residential IP or the Cloudflare worker, and
 * a `data:`/`blob:` URL only exists in this tab. A reference must never lose an
 * image it could have had under the old path.
 *
 * Every image logs one `[import-image]` line with its fetch / upload / total
 * milliseconds and which route it took, so a slow run explains itself.
 *
 * Firestore is untouched — callers write `images` / `galleryImages` themselves,
 * exactly as they did after their own uploads.
 */

import { httpsCallable } from 'firebase/functions';
import {
  fetchImageBlob,
  functions,
  getImageExtension,
  uploadReferenceImage,
} from './firebase';

export type IngestImageKind = 'main' | 'gallery';

export interface IngestImageRequest {
  referenceId: string;
  imageUrl: string;
  kind: IngestImageKind;
  index: number;
}

export type IngestRoute = 'server' | 'browser';

export type IngestImageOutcome =
  | {
      ok: true;
      storagePath: string;
      contentType: string;
      route: IngestRoute;
      fetchMs: number;
      uploadMs: number;
      totalMs: number;
      sizeBytes?: number;
    }
  | { ok: false; error: string; route: IngestRoute; totalMs: number };

/** One item of the callable's response — see functions/src/ingestReferenceImagesCore.ts. */
export interface ServerIngestResult extends IngestImageRequest {
  ok: boolean;
  storagePath?: string;
  contentType?: string;
  sizeBytes?: number;
  errorCode?: string;
  error?: string;
  fetchMs: number;
  uploadMs: number;
  totalMs: number;
}

/** Mirrors MAX_INGEST_ITEMS on the server. */
export const MAX_INGEST_ITEMS_PER_CALL = 12;

/**
 * Server verdicts the browser path would only repeat: the bytes were fetched
 * fine and are simply not a usable image. Everything else (blocked host,
 * timeout, a failed call) is worth a second route.
 */
// 'blocked': the server already climbed the whole fetch ladder (browser headers,
// Cloudflare Worker, image CDN) — the browser route ends at the same walls.
// 'job-image-missing': an extractor-job image that is no longer in Storage; the
// browser has no route to it either (the job folder is not client-readable).
const FINAL_SERVER_ERRORS: ReadonlySet<string> = new Set([
  'not-an-image',
  'too-large',
  'blocked',
  'job-image-missing',
]);

export interface IngestDeps {
  callServer: (brandId: string, items: IngestImageRequest[]) => Promise<ServerIngestResult[]>;
  fetchBlob: (url: string) => Promise<Blob>;
  upload: (brandId: string, referenceId: string, file: File) => Promise<string>;
  now: () => number;
  log: (line: string) => void;
}

async function callIngestCallable(
  brandId: string,
  items: IngestImageRequest[],
): Promise<ServerIngestResult[]> {
  const fn = httpsCallable<
    { brandId: string; items: IngestImageRequest[] },
    { results: ServerIngestResult[] }
  >(functions, 'ingestReferenceImages', { timeout: 120_000 });
  const response = await fn({ brandId, items });
  return response.data.results;
}

const defaultDeps: IngestDeps = {
  callServer: callIngestCallable,
  fetchBlob: fetchImageBlob,
  upload: uploadReferenceImage,
  now: () => Date.now(),
  log: (line) => console.info(line),
};

/**
 * Whether the URL names an image an extractor job downloaded into Storage
 * (`extractor-jobs/{jobId}/images/…`), in any of the spellings the server
 * accepts. Only the server can promote these: the job folder is not readable
 * from the browser, and a `gs://` URL is not fetchable at all.
 */
export function isExtractorJobImageUrl(url: string): boolean {
  const value = url.trim();
  if (value.startsWith('extractor-jobs/')) return true;
  if (/^gs:\/\/[^/]+\/extractor-jobs\//.test(value)) return true;
  // firebasestorage download URLs carry the object path %2F-encoded.
  return /^https:\/\/(firebasestorage|storage)\.googleapis\.com\/.*extractor-jobs(%2F|\/)/i.test(value);
}

function isServerFetchable(url: string): boolean {
  return /^https?:\/\//i.test(url.trim()) || isExtractorJobImageUrl(url);
}

function megabytes(bytes: number | undefined): string {
  return bytes == null ? '' : ` ${(bytes / 1024 / 1024).toFixed(2)}MB`;
}

/** The single line each image leaves in the import log. */
export function formatIngestLogLine(
  request: IngestImageRequest,
  outcome: IngestImageOutcome,
  note?: string,
): string {
  const head = `[import-image] ${request.referenceId} ${request.kind}#${request.index} ${outcome.route}`;
  const tail = note ? ` (${note})` : '';
  if (!outcome.ok) return `${head} FAILED total=${outcome.totalMs}ms — ${outcome.error}${tail}`;
  return (
    `${head} fetch=${outcome.fetchMs}ms upload=${outcome.uploadMs}ms total=${outcome.totalMs}ms` +
    `${megabytes(outcome.sizeBytes)}${tail}`
  );
}

async function ingestInBrowser(
  brandId: string,
  request: IngestImageRequest,
  deps: IngestDeps,
): Promise<IngestImageOutcome> {
  const started = deps.now();
  try {
    const blob = await deps.fetchBlob(request.imageUrl);
    const fetched = deps.now();
    const file = new File([blob], `image.${getImageExtension(request.imageUrl)}`, {
      type: blob.type || 'image/jpeg',
    });
    const storagePath = await deps.upload(brandId, request.referenceId, file);
    const done = deps.now();
    return {
      ok: true,
      storagePath,
      contentType: file.type,
      route: 'browser',
      fetchMs: fetched - started,
      uploadMs: done - fetched,
      totalMs: done - started,
      sizeBytes: blob.size,
    };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
      route: 'browser',
      totalMs: deps.now() - started,
    };
  }
}

/**
 * Ingest a set of images for ONE brand. Returns one outcome per request, in
 * request order; never throws for a single bad image.
 *
 * The server stores a call's images concurrently, so handing it a reference's
 * whole gallery in one go is what removes the old one-at-a-time wait.
 */
export async function ingestReferenceImages(
  brandId: string,
  requests: readonly IngestImageRequest[],
  overrides: Partial<IngestDeps> = {},
): Promise<IngestImageOutcome[]> {
  const deps: IngestDeps = { ...defaultDeps, ...overrides };
  const outcomes: (IngestImageOutcome | undefined)[] = requests.map(() => undefined);
  /** Why an image that reached the browser path did not stay on the server. */
  const fallbackNote = new Map<number, string>();

  const forServer = requests
    .map((request, position) => ({ request, position }))
    .filter(({ request }) => isServerFetchable(request.imageUrl));

  for (let start = 0; start < forServer.length; start += MAX_INGEST_ITEMS_PER_CALL) {
    const chunk = forServer.slice(start, start + MAX_INGEST_ITEMS_PER_CALL);
    const callStarted = deps.now();
    let results: ServerIngestResult[] | null = null;
    let callError = '';
    try {
      results = await deps.callServer(brandId, chunk.map(({ request }) => request));
    } catch (err) {
      callError = err instanceof Error ? err.message : String(err);
    }
    chunk.forEach(({ position }, i) => {
      const result = results?.[i];
      if (result?.ok && result.storagePath) {
        outcomes[position] = {
          ok: true,
          storagePath: result.storagePath,
          contentType: result.contentType ?? 'image/jpeg',
          route: 'server',
          fetchMs: result.fetchMs,
          uploadMs: result.uploadMs,
          totalMs: result.totalMs,
          sizeBytes: result.sizeBytes,
        };
        return;
      }
      const error = result?.error ?? (callError || 'No result returned');
      if (result?.errorCode && FINAL_SERVER_ERRORS.has(result.errorCode)) {
        outcomes[position] = {
          ok: false,
          error,
          route: 'server',
          totalMs: result.totalMs,
        };
        return;
      }
      fallbackNote.set(
        position,
        `server ${result?.errorCode ?? 'call-failed'} after ${result?.totalMs ?? deps.now() - callStarted}ms: ${error}`,
      );
    });
  }

  // Whatever the server could not take, one at a time — the browser path is the
  // slow one, and it is the curator's uplink being shared.
  for (let position = 0; position < requests.length; position += 1) {
    if (outcomes[position]) continue;
    outcomes[position] = await ingestInBrowser(brandId, requests[position], deps);
  }

  return requests.map((request, position) => {
    const outcome = outcomes[position] as IngestImageOutcome;
    deps.log(formatIngestLogLine(request, outcome, fallbackNote.get(position)));
    return outcome;
  });
}

/**
 * Ingest one image, throwing when it could not be had by either route — the
 * shape the main-image call sites want, since each already has a catch that
 * records `pendingImageUrl`.
 */
export async function ingestReferenceImage(
  brandId: string,
  request: IngestImageRequest,
  overrides: Partial<IngestDeps> = {},
): Promise<{ storagePath: string; contentType: string }> {
  const [outcome] = await ingestReferenceImages(brandId, [request], overrides);
  if (!outcome.ok) throw new Error(outcome.error);
  return { storagePath: outcome.storagePath, contentType: outcome.contentType };
}

/** Whether a probe found the image would import. */
export type ImageProbeResult = { ok: true } | { ok: false; error: string; errorCode?: string };

export interface ProbeDeps {
  callServer: (
    brandId: string,
    items: IngestImageRequest[],
  ) => Promise<ServerIngestResult[]>;
}

async function callProbeCallable(
  brandId: string,
  items: IngestImageRequest[],
): Promise<ServerIngestResult[]> {
  // Its own callable, never a flag on ingestReferenceImages: an older deploy of
  // that one would ignore the flag and store every probed image.
  const fn = httpsCallable<
    { brandId: string; items: IngestImageRequest[] },
    { results: ServerIngestResult[] }
  >(functions, 'probeReferenceImages', { timeout: 120_000 });
  const response = await fn({ brandId, items });
  return response.data.results;
}

/** Calls in flight at once while probing — each is up to 12 server-side fetches. */
const PROBE_CONCURRENCY = 3;

/**
 * Ask the server whether each URL would import, WITHOUT storing anything — the
 * bulk-import preview's per-row "this ref will commit without an image" check.
 * Runs the same fetch ladder (or job-folder read) and the same image checks as
 * a real import.
 *
 * A server failure is reported as a failure even where the real import would
 * still try the browser route afterwards: hosts that refuse the cloud almost
 * always refuse the browser's `fetch` too (CORS), and an over-warning preview is
 * the cheaper mistake. URLs the server cannot take at all (`data:`, `blob:`)
 * are left out of the result — unknown, not failed.
 */
export async function probeReferenceImages(
  brandId: string,
  urls: readonly string[],
  overrides: Partial<ProbeDeps> = {},
): Promise<Map<string, ImageProbeResult>> {
  const callServer = overrides.callServer ?? callProbeCallable;
  const unique = [...new Set(urls.map((u) => u.trim()).filter((u) => u && isServerFetchable(u)))];
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += MAX_INGEST_ITEMS_PER_CALL) {
    chunks.push(unique.slice(i, i + MAX_INGEST_ITEMS_PER_CALL));
  }

  const results = new Map<string, ImageProbeResult>();
  let next = 0;
  const worker = async () => {
    while (next < chunks.length) {
      const chunk = chunks[next++];
      try {
        const answers = await callServer(
          brandId,
          // The id only has to pass validation — a probe writes nothing.
          chunk.map((imageUrl, index) => ({ referenceId: 'probe', imageUrl, kind: 'main', index })),
        );
        chunk.forEach((url, i) => {
          const answer = answers[i];
          if (answer?.ok) results.set(url, { ok: true });
          else
            results.set(url, {
              ok: false,
              error: answer?.error ?? 'No result returned',
              ...(answer?.errorCode ? { errorCode: answer.errorCode } : {}),
            });
        });
      } catch (err) {
        // A failed CALL says nothing about the images; leave them unknown so
        // the preview can offer to re-check rather than flag every row.
        console.warn('Image probe call failed:', err);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(PROBE_CONCURRENCY, chunks.length) }, worker));
  return results;
}
